import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Plus, Download, Tag, Calendar, GitCommit, 
  ChevronDown, ChevronRight, X, Bot, Search, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { projectAPI } from '../lib/api';

export default function ReleaseNotesPanel({ projectId, canEdit = true }) {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [expandedRelease, setExpandedRelease] = useState(null);
  const [newRelease, setNewRelease] = useState({
    version: '',
    tag: '',
    title: '',
    date: new Date().toISOString().split('T')[0],
    features: '',
    improvements: '',
    bugfixes: '',
    breaking_changes: '',
    notes: '',
  });

  useEffect(() => {
    loadReleases();
  }, [projectId]);

  const loadReleases = async () => {
    try {
      const res = await projectAPI.getReleaseNotes(projectId);
      setReleases(res.data || []);
    } catch (err) {
      console.log('Loading releases:', err);
      setReleases([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRelease = async () => {
    if (!newRelease.version.trim()) {
      toast.error('Version is required');
      return;
    }
    try {
      await projectAPI.addReleaseNote(projectId, newRelease);
      setShowAddModal(false);
      setNewRelease({
        version: '', tag: '', title: '',
        date: new Date().toISOString().split('T')[0],
        features: '', improvements: '', bugfixes: '', breaking_changes: '', notes: '',
      });
      await loadReleases();
      toast.success('Release notes added');
    } catch (err) {
      toast.error('Failed to add release notes');
    }
  };

  const handleGenerateNotes = async () => {
    setGenerating(true);
    try {
      const res = await projectAPI.generateReleaseNotes(projectId, { version: newRelease.version || 'Next Release' });
      if (res.data?.content) {
        // Parse the generated content
        const content = res.data.content;
        setNewRelease(prev => ({
          ...prev,
          features: extractSection(content, 'Features', 'New Features') || prev.features,
          improvements: extractSection(content, 'Improvements', 'Enhancements') || prev.improvements,
          bugfixes: extractSection(content, 'Bug Fixes', 'Fixes') || prev.bugfixes,
          notes: content,
        }));
        toast.success('Release notes generated!');
      }
    } catch (err) {
      toast.error('Failed to generate release notes');
    } finally {
      setGenerating(false);
    }
  };

  const extractSection = (content, ...keywords) => {
    for (const keyword of keywords) {
      const regex = new RegExp(`#+\\s*${keyword}[^#]*`, 'i');
      const match = content.match(regex);
      if (match) {
        return match[0].replace(/#+\s*\w+\s*\n/, '').trim();
      }
    }
    return '';
  };

  const handleDelete = async (releaseId) => {
    try {
      await projectAPI.deleteReleaseNote(projectId, releaseId);
      await loadReleases();
      toast.success('Deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const downloadReleaseNotes = (release) => {
    const content = `# Release ${release.version}${release.title ? ` - ${release.title}` : ''}

**Tag:** ${release.tag || 'N/A'}
**Date:** ${new Date(release.date).toLocaleDateString()}

## New Features
${release.features || 'No new features in this release.'}

## Improvements
${release.improvements || 'No improvements in this release.'}

## Bug Fixes
${release.bugfixes || 'No bug fixes in this release.'}

${release.breaking_changes ? `## Breaking Changes\n${release.breaking_changes}` : ''}

${release.notes ? `## Notes\n${release.notes}` : ''}
`;

    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `release-notes-${release.version}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[var(--accent-indigo)] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="h-full flex flex-col" data-testid="release-notes-panel">
      {/* Header */}
      <div className="p-4 border-b border-[var(--surface-border)]">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-semibold text-left">Release Notes</h2>
            <p className="text-xs opacity-60 text-left">Track version history and changes</p>
          </div>
          {canEdit && (
            <Button size="sm" onClick={() => setShowAddModal(true)}>
              <Plus className="w-3.5 h-3.5 mr-1.5" /> New Release
            </Button>
          )}
        </div>
      </div>

      {/* Timeline */}
      <ScrollArea className="flex-1">
        <div className="p-4">
          {releases.length === 0 ? (
            <div className="text-center py-12 opacity-60">
              <Tag className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No releases yet</p>
              {canEdit && <p className="text-xs mt-1">Click "New Release" to document your first release</p>}
            </div>
          ) : (
            <div className="space-y-4">
              {releases.map((release, index) => (
                <div
                  key={release.id}
                  className="relative pl-6 pb-4 border-l-2 border-[var(--surface-border)] last:border-transparent"
                >
                  {/* Timeline dot */}
                  <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-[var(--accent-indigo)] border-2 border-[var(--surface-bg)]" />
                  
                  <div className="bg-[var(--surface-card)] border border-[var(--surface-border)] rounded-lg overflow-hidden">
                    {/* Header */}
                    <div
                      className="p-4 cursor-pointer hover:bg-[var(--surface-bg)]/50"
                      onClick={() => setExpandedRelease(expandedRelease === release.id ? null : release.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {expandedRelease === release.id ? (
                            <ChevronDown className="w-4 h-4 opacity-50" />
                          ) : (
                            <ChevronRight className="w-4 h-4 opacity-50" />
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <Badge className="bg-[var(--accent-indigo)]/10 text-[var(--accent-indigo)] border-[var(--accent-indigo)]/20">
                                v{release.version}
                              </Badge>
                              {release.tag && (
                                <Badge variant="outline" className="text-[10px]">
                                  <GitCommit className="w-3 h-3 mr-1" />
                                  {release.tag}
                                </Badge>
                              )}
                              {index === 0 && (
                                <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">Latest</Badge>
                              )}
                            </div>
                            {release.title && (
                              <h3 className="font-medium mt-1 text-left">{release.title}</h3>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs opacity-50 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(release.date).toLocaleDateString()}
                          </span>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); downloadReleaseNotes(release); }}>
                            <Download className="w-3.5 h-3.5" />
                          </Button>
                          {canEdit && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={(e) => { e.stopPropagation(); handleDelete(release.id); }}>
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {expandedRelease === release.id && (
                      <div className="p-4 pt-0 border-t border-[var(--surface-border)]">
                        {release.features && (
                          <div className="mb-4">
                            <h4 className="text-sm font-semibold mb-2 flex items-center gap-2 text-left">
                              <Sparkles className="w-4 h-4 text-emerald-400" /> New Features
                            </h4>
                            <div className="text-sm opacity-70 whitespace-pre-wrap text-left">{release.features}</div>
                          </div>
                        )}
                        {release.improvements && (
                          <div className="mb-4">
                            <h4 className="text-sm font-semibold mb-2 text-left">Improvements</h4>
                            <div className="text-sm opacity-70 whitespace-pre-wrap text-left">{release.improvements}</div>
                          </div>
                        )}
                        {release.bugfixes && (
                          <div className="mb-4">
                            <h4 className="text-sm font-semibold mb-2 text-left">Bug Fixes</h4>
                            <div className="text-sm opacity-70 whitespace-pre-wrap text-left">{release.bugfixes}</div>
                          </div>
                        )}
                        {release.breaking_changes && (
                          <div className="mb-4">
                            <h4 className="text-sm font-semibold mb-2 text-red-400 text-left">Breaking Changes</h4>
                            <div className="text-sm opacity-70 whitespace-pre-wrap text-left">{release.breaking_changes}</div>
                          </div>
                        )}
                        {release.notes && (
                          <div>
                            <h4 className="text-sm font-semibold mb-2 text-left">Notes</h4>
                            <div className="text-sm opacity-70 whitespace-pre-wrap text-left">{release.notes}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[var(--surface-card)] rounded-xl border border-[var(--surface-border)] w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">New Release Notes</h3>
              <Button variant="ghost" size="icon" onClick={() => setShowAddModal(false)}><X className="w-4 h-4" /></Button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-medium mb-1 block text-left">Version *</label>
                  <Input value={newRelease.version} onChange={(e) => setNewRelease({ ...newRelease, version: e.target.value })} placeholder="e.g., 1.0.0" />
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block text-left">Git Tag</label>
                  <Input value={newRelease.tag} onChange={(e) => setNewRelease({ ...newRelease, tag: e.target.value })} placeholder="e.g., v1.0.0" />
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block text-left">Release Date</label>
                  <Input type="date" value={newRelease.date} onChange={(e) => setNewRelease({ ...newRelease, date: e.target.value })} />
                </div>
              </div>
              
              <div>
                <label className="text-xs font-medium mb-1 block text-left">Title</label>
                <Input value={newRelease.title} onChange={(e) => setNewRelease({ ...newRelease, title: e.target.value })} placeholder="e.g., Initial Release, Bug Fix Release" />
              </div>

              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-left">Release Content</label>
                <Button variant="outline" size="sm" onClick={handleGenerateNotes} disabled={generating}>
                  <Bot className={`w-3.5 h-3.5 mr-1.5 ${generating ? 'animate-spin' : ''}`} />
                  {generating ? 'Generating...' : 'Generate with AI'}
                </Button>
              </div>

              <div>
                <label className="text-xs font-medium mb-1 block text-left">New Features</label>
                <Textarea value={newRelease.features} onChange={(e) => setNewRelease({ ...newRelease, features: e.target.value })} placeholder="List new features..." rows={3} />
              </div>

              <div>
                <label className="text-xs font-medium mb-1 block text-left">Improvements</label>
                <Textarea value={newRelease.improvements} onChange={(e) => setNewRelease({ ...newRelease, improvements: e.target.value })} placeholder="List improvements and enhancements..." rows={3} />
              </div>

              <div>
                <label className="text-xs font-medium mb-1 block text-left">Bug Fixes</label>
                <Textarea value={newRelease.bugfixes} onChange={(e) => setNewRelease({ ...newRelease, bugfixes: e.target.value })} placeholder="List bug fixes..." rows={3} />
              </div>

              <div>
                <label className="text-xs font-medium mb-1 block text-left">Breaking Changes (if any)</label>
                <Textarea value={newRelease.breaking_changes} onChange={(e) => setNewRelease({ ...newRelease, breaking_changes: e.target.value })} placeholder="List any breaking changes..." rows={2} />
              </div>

              <div>
                <label className="text-xs font-medium mb-1 block text-left">Additional Notes</label>
                <Textarea value={newRelease.notes} onChange={(e) => setNewRelease({ ...newRelease, notes: e.target.value })} placeholder="Any additional notes..." rows={2} />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button onClick={handleAddRelease}>Publish Release Notes</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
